import * as THREE from "three";
import { groundAt } from "../render/terrain";
import { LevelPhase, LevelSim, SimConstants, SimEventKind, endlessPool } from "@highmarch/domain";
import { SceneRig } from "../render/scene";
import { GridView, hqTowerName, lib, surfaceY } from "../render/views";
import { HudView } from "../ui/hud";
import { chapterOf } from "../ui/chapters";
import { reducedVfx } from "../ui/settings";
import { waveChips } from "../ui/wavePreview";
import { rulesLine, runChallenge } from "../ui/modes/challengeWords";
import { dailyDayLabel } from "../ui/modes/dailyDate";
import { audio } from "../audio/audio";
import { HitStop } from "./hitStop";
import { HpHistory, Moment, MomentBudget, MomentTracker, MomentWorld, heartbeatInterval, lastStandDepth } from "./moments";
import type { GuideSignal } from "../ui/guide/guide";
import { KillLanding, LandingWait, afterLandings } from "./impactWaits";
import { weakTo } from "@highmarch/app";
import type { BalanceData } from "@highmarch/domain";
import { appHeight } from "../ui/appSize";
import { isFinalStage, stageLine } from "../ui/runMode";
import { isRareArchetype, isRareEnemy } from "../rare/rareApi";

/** The counter hint for a boss (G23): "Weak to Frost · Cannon · Ballista" from the shared table (app/counters.ts). */
export function bossHint(balance: BalanceData, enemyId: string): string {
  const towers = weakTo(balance, enemyId).towers;
  return towers.length > 0 ? `Weak to ${towers.map((t) => t.name).join(" · ")}` : "";
}

/** Level-intro card (art/interface drop 2 §3) for 1.8 s over the island before the first offer. */
export function showIntro(hud: HudView, sim: LevelSim, level: number): Promise<void> {
  const B = sim.balance;
  const ch = chapterOf(B, level, B.levels.length);
  const def = sim.level;
  const enemyIds = [...new Set(def.groups.map((g) => B.enemies[g.enemyIndex].id))].slice(0, 4);
  const boss = level === ch.last ? B.enemies[def.bossEnemyIndex] : null;
  // M21 (docs/MODES.md): an Endless run names its own fight; an Iron run its one wave and three towers.
  if (sim.isEndless) {
    const pool = endlessPool(B, sim.mods.endless!.through).map((i) => B.enemies[i].id).slice(-4);
    return hud.intro({ chapter: ch.index + 1, chapterName: ch.name, biome: ch.biome, level, ribbon: "ENDLESS", title: "Hold the line", copy: `Waves without end · a boss every ${B.modes.endless.bossEvery}`, enemyIds: pool, bossName: null, opensChapter: false });
  }
  if (sim.isDaily) {
    // MP-20: the day's road — the same for every commander, every tower at the fair level, no gear (VIP counts: D1)
    return hud.intro({ chapter: ch.index + 1, chapterName: ch.name, biome: ch.biome, level, ribbon: `DAILY ROAD · ${dailyDayLabel(sim.mods.daily!.day).toUpperCase()}`, title: "Today's road", copy: `${sim.stages} waves · every tower at level ${B.modes.normalised.towerLevel} · no gear${B.modes.normalised.allowVip ? " · VIP counts" : ""}`, enemyIds, bossName: B.enemies[def.bossEnemyIndex].name, bossHint: bossHint(B, B.enemies[def.bossEnemyIndex].id), opensChapter: false });
  }
  const challenge = runChallenge(sim.mods.challenge?.code);
  if (challenge) {
    // MP-28: a challenge code's run — the same battle for everyone who enters the code, its rules on the card
    return hud.intro({ chapter: ch.index + 1, chapterName: ch.name, biome: ch.biome, level, ribbon: `CHALLENGE · LEVEL ${level}`, title: "A shared challenge", copy: `${sim.stages} waves · ${rulesLine(B, challenge)}`, enemyIds, bossName: B.enemies[def.bossEnemyIndex].name, bossHint: bossHint(B, B.enemies[def.bossEnemyIndex].id), opensChapter: false });
  }
  if (sim.isIron) {
    const towers = sim.mods.iron!.towers.map((i) => B.towers[i].name).join(" · ");
    return hud.intro({ chapter: ch.index + 1, chapterName: ch.name, biome: ch.biome, level, ribbon: `IRON · LEVEL ${level}`, title: "One long wave", copy: `${B.stagesOf(level)} waves at once · ${towers}`, enemyIds, bossName: B.enemies[def.bossEnemyIndex].name, bossHint: bossHint(B, B.enemies[def.bossEnemyIndex].id), opensChapter: false });
  }
  return hud.intro({ chapter: ch.index + 1, chapterName: ch.name, biome: ch.biome, level, copy: `${B.stagesOf(level)} waves · lay the road, then hold it`, enemyIds, bossName: boss ? boss.name : null, bossHint: boss ? bossHint(B, boss.id) : "", opensChapter: level === ch.first });
}

/**
 * Centre-screen lines for wave arrivals (number and composition), boss arrivals and bombed towers; a floating TIER n
 * ribbon over an upgraded tower; a red wash when the fortress is hit. Reads the tick's events from `from` on (the
 * level's EventCursor), so a command landing mid-tick never replays the tick's earlier ones.
 */
export function announceEvents(hud: HudView, sim: LevelSim, rig: SceneRig, grid: GridView, from = 0): void {
  const events = sim.events.all();
  for (let i = from; i < events.length; i++) {
    const ev = events[i];
    if (ev.kind === SimEventKind.TowerUpgraded) {
      const t = sim.findTower(ev.a);
      if (!t) continue;
      const a = sim.balance.towers[t.archetype];
      const h = lib()?.height(hqTowerName(a.id, a.isBase, t.tier)) ?? 1.2;
      const v = new THREE.Vector3(t.cell.x, groundAt(t.cell.x, t.cell.y) + h * 0.8, t.cell.y).project(rig.camera);
      // tier IV (M21) names the branch the tower became: "TIER IV · RANGER LODGE"
      const branch = t.tier >= 4 ? sim.balance.branches.branches[sim.ascension.branchOf[t.archetype]] : undefined;
      hud.floatRibbon(branch ? `TIER IV · ${branch.name.toUpperCase()}` : `TIER ${t.tier}`, ((v.x + 1) / 2) * innerWidth, ((1 - v.y) / 2) * appHeight());
    } else if (ev.kind === SimEventKind.BossSpawned) {
      if (isRareArchetype(sim.balance, ev.b) || isRareEnemy(sim, ev.a)) continue;   // M36: the rare has its own banner (rareMoment.ts)
      const boss = sim.balance.enemies[ev.b];
      const hint = bossHint(sim.balance, boss.id);
      hud.announce(`BOSS · ${boss.name}`, "boss", 2200, hint ? `${stageLine(sim)}<br>${hint}` : `${stageLine(sim)} — hold the line`, boss.id);
      if (!reducedVfx()) rig.shake();
      grid.foliage.gustAround(sim.road.portal);
    } else if (ev.kind === SimEventKind.StageStarted) {
      hud.waveIncoming(sim.isIron ? 0 : ev.a, isFinalStage(sim, ev.a) ? ev.a : -1, waveChips(sim.balance, sim.schedule));
    } else if (ev.kind === SimEventKind.EnemyLeaked) {
      hud.hitFlash();
    } else if (ev.kind === SimEventKind.TowerDisabled && ev.c === 0) {          // a Bomber's bomb (a Stunner's names itself in c)
      const t = sim.findTower(ev.a);
      if (t) hud.announce(`${sim.balance.towers[t.archetype].name} knocked out · ${Math.round(ev.b / SimConstants.TicksPerSecond)} s`, "warn", 1400);
    } else if (ev.kind === SimEventKind.EnemyStomped && ev.b > 0) {           // one line for the whole shockwave (ENEMIES_V2)
      const e = sim.enemySlots.find((x) => x.id === ev.a);
      const secs = e ? Math.round(sim.balance.enemies[e.archetype].abilities.stompDisableTicks / SimConstants.TicksPerSecond) : 0;
      hud.announce(`Stunned! ${ev.b} tower${ev.b === 1 ? "" : "s"}${secs ? ` for ${secs} s` : ""}`, "warn", 1400);
    }
  }
}

/**
 * The guide's signals in a tick's (or a command's) events: a boss arriving, a wave starting and the first spawn of each
 * archetype this run (`enemy:<id>`). One per level run: it remembers which archetypes it has already announced.
 */
export class GuideEventSignals {
  private readonly seen = new Set<number>();

  observe(sim: LevelSim, raise: (s: GuideSignal) => void, from = 0): void {
    const events = sim.events.all();
    for (let i = from; i < events.length; i++) {
      const ev = events[i];
      if (ev.kind === SimEventKind.BossSpawned) raise("bossSpawned");
      else if (ev.kind === SimEventKind.StageStarted) raise("stageStarted");
      else if (ev.kind === SimEventKind.EnemySpawned && !this.seen.has(ev.b)) {
        this.seen.add(ev.b);                                  // b = the archetype (systems/enemyMotion.ts)
        raise(`enemy:${sim.balance.enemies[ev.b].id}`);
      }
    }
  }
}

/** The tracker's view of a LevelSim: road distance left for a body, bosses, tower names. */
function momentWorld(sim: LevelSim): MomentWorld {
  return {
    tick: sim.tick,
    ticksPerSecond: SimConstants.TicksPerSecond,
    // a body keeps its slot (and its last position) through the tick it died in
    cellsToBase: (id) => { const e = sim.enemySlots.find((x) => x.id === id); return e ? sim.road.length.toNumber() - e.distance.toNumber() : null; },
    isBoss: (id) => (sim.enemySlots.find((x) => x.id === id)?.isBoss ?? false) && !isRareEnemy(sim, id),   // M36: the rare's fall is its own moment
    towerName: (id) => { const t = sim.findTower(id); return t ? sim.balance.towers[t.archetype].name : ""; },
  };
}

/**
 * The level's moments on screen and in the speakers (ENGAGEMENT §B8): the last stand — heartbeat, music low-pass and
 * the red vignette pulsing with the beat while the fortress is at ≤ 25 % in a wave — the budgeted callouts, the SAVED!
 * hit-stop, and the end verdict. The rules are in moments.ts; this only presents them. Presentation only: it reads
 * the sim and never writes it. `stop` must run when the level ends or goes, so nothing carries into the next one.
 */
export class LevelMoments {
  private readonly tracker = new MomentTracker();
  private readonly budget = new MomentBudget();
  private readonly hp = new HpHistory();
  /** Seconds until the next heartbeat while the last stand holds. */
  private beatIn = 0;
  private depth: number | null = null;
  /** Set by the level: the last stand began (the guide's `lastStand` signal). */
  onLastStand: (() => void) | null = null;

  constructor(private readonly hud: HudView, private readonly hitStop: HitStop) {}

  /** The fortress is in its last stand now. */
  get lastStand(): boolean { return this.depth !== null; }

  /** The run's best moments so far, best first (at most three), for the share card. */
  get top(): readonly Moment[] { return this.budget.top; }

  /**
   * After a sim tick or a command: reads the buffer's events from `from` on. A moment born of kills whose rounds are
   * still in the air (`landings`) is called out when they land (`wait`), not as the tower fires: the SAVED! hit-stop
   * used to freeze the frame before the killing arrow reached the raider.
   */
  observe(sim: LevelSim, from = 0, landings: readonly KillLanding[] = [], wait: LandingWait | null = null): void {
    this.hp.note(sim.baseHp, sim.maxBaseHp);
    if (sim.events.count <= from) return;
    const all = sim.events.all(), events = from === 0 ? all : all.slice(from);
    const moments = this.tracker.observe(events, momentWorld(sim));
    if (moments.length === 0) return;
    const offer = () => {
      for (const m of moments) {
        this.budget.offer(m);
        if (m.kind === "saved" && !this.stopped) this.hitStop.request(HitStop.Ms.saved, { ramp: true });
      }
    };
    if (wait) afterLandings(landings, wait, offer); else offer();
  }
  /** Set by `stop`: a moment whose round lands after the level ended is still ranked, but freezes nothing. */
  private stopped = false;

  /** Every frame the level runs: the budget's clock, the callout due, and the last stand's heartbeat. */
  frame(dt: number, sim: LevelSim, paused: boolean): void {
    const shown = this.budget.update(paused ? 0 : dt, paused || sim.currentOffer !== null);
    if (shown) this.hud.showCallout(shown.title, shown.sub, shown.kind);
    const depth = sim.phase === LevelPhase.WaveRunning ? lastStandDepth(sim.baseHp, sim.maxBaseHp) : null;
    if (depth !== this.depth) {
      const began = this.depth === null;
      if (began) this.beatIn = 0;                             // the first beat lands as the stand begins
      this.depth = depth;
      if (began) this.onLastStand?.();
      this.hud.setLastStand(depth);
      audio.lastStand(depth);
    }
    if (depth === null || paused) return;
    this.beatIn -= dt;
    if (this.beatIn > 0) return;
    this.beatIn += heartbeatInterval(depth);
    if (this.beatIn < 0) this.beatIn = heartbeatInterval(depth);   // a long frame never queues a burst of beats
    audio.heartbeat(depth);
    this.hud.heartbeatPulse();
  }

  /** The level is over (or gone): the last stand lets go, nothing more is called out. Idempotent. */
  stop(): void {
    this.stopped = true;
    this.budget.clearPending();
    if (this.depth !== null) this.hud.setLastStand(null);
    this.depth = null;
    audio.lastStand(null);
  }

  /** The end verdict for the victory screen (CLUTCH VICTORY / COMEBACK), ranked among the run's moments; null for none. */
  verdict(sim: LevelSim): Moment | null {
    const m = this.hp.verdict(sim.phase === LevelPhase.Won, sim.baseHp, sim.maxBaseHp);
    if (m) this.budget.record(m);
    return m;
  }
}
